<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name, position: 'top', subposition: 'right', allowOtherLeft: true }"
    :button-bind="{ icon, tooltip, verboseName: tooltip, ...(isMenuOpen ? { iconColor: 'error' } : {}) }"
    :loading="loading"
    has-close-header
    documentation-position="header"
    unfill-width
  >
    <template #[`content.header.prepend`]>
      <v-col cols="auto" class="text-body-2 pr-1" style="padding-top: 2px">
        {{ tooltip }}
      </v-col>
    </template>
    <template #content>
      <v-form ref="form" v-model="valid">
        <v-row no-gutters class="px-4 py-3 flex-nowrap" align="center">
          <v-col cols="auto" v-for="(input, idx) in inputs" :key="idx" class="mr-1" :style="`width: ${input.width}`">
            <data-input v-model="params[input.name]" v-bind="input" @change="onParamChange(input, $event)" />
          </v-col>
          <base-button
            class="mr-0"
            icon="mdi-play"
            icon-size="22px"
            :button-style="{
              backgroundColor: valid ? $_colors.primary : 'rgb(210, 210, 210)',
              iconColor: 'white',
              borderRadius: '8px',
            }"
            is-icon
            :disabled="!valid || loading"
            @click="run()"
          />
        </v-row>
      </v-form>
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import NavbarMenuTool from '@/components/NavbarMenuTool';

import { GeoJSON } from 'ol/format';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'TheNavbarToolsPanelHeatmap',
  components: { NavbarMenuTool, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  props: {
    icon: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
  },
  computed: {
    datasources: get('layers/metadata'),
    datasourceName() {
      return this.layer?.data_source_name || '';
    },
    datasource() {
      return this.datasources[this.datasourceName] || undefined;
    },
    layersFilters: get('layers/layersFilters'),
    layers: get('layers/layers'),
    layer() {
      return this.layers[this.params.layer] || undefined;
    },
    layerId() {
      return this.layer?.id || null;
    },
    layerDecAttributes() {
      return this.$_flattenStructure(
        this.getForm(this.layer?.form_schema?.elements || [], this.datasource?.attributes_schema?.attributes || [])
      ).filter(attribute => ['decimal', 'float'].includes(attribute.dataType.name));
    },
    projectElements: get('layers/project@layers'),
    pointProjectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers.filter(layer => layer.geometry_type === 'point');
    },
    inputs() {
      const defaults = { persistentPlaceholder: true, hideDetails: true, readonly: this.loading };
      return [
        {
          ...defaults,
          name: 'layer',
          dataType: { name: 'select' },
          label: this.$i18n.t('default.layer'),
          width: '200px',
          items: this.pointProjectLayers,
          itemValue: 'id',
          itemText: 'name',
          rules: [v => !!v || ''],
        },
        {
          ...defaults,
          name: 'weight',
          dataType: { name: 'select' },
          label: this.$i18n.t('sidebar.heatmap.weight'),
          width: '200px',
          items: this.layerDecAttributes,
          itemValue: 'attribute',
          itemText: 'label',
          clearable: true,
        },
        {
          ...defaults,
          name: 'radius',
          dataType: { name: 'integer' },
          allowNegative: false,
          minValue: 1,
          maxValue: 40,
          label: this.$i18n.t('sidebar.heatmap.radius'),
          width: '90px',
        },
        {
          ...defaults,
          name: 'blur',
          dataType: { name: 'integer' },
          allowNegative: false,
          minValue: 1,
          maxValue: 50,
          label: this.$i18n.t('sidebar.heatmap.blur'),
          width: '90px',
        },
      ];
    },
  },
  data: () => ({
    isMenuOpen: false,
    loading: false,
    lastParams: undefined,
    params: {
      layer: null,
      weight: '',
      radius: 2,
      blur: 6,
    },
    valid: false,
  }),
  methods: {
    getDataSourceAttributeStatistics: call('layers/getDataSourceAttributeStatistics'),
    getV2DatasourceFeatures: call('layers/getV2DatasourceFeatures'),
    async run(params = this.params) {
      if (!this.valid) return;
      this.loading = true;
      let features;
      try {
        const { layer: layerId, ...restParams } = params;
        if (layerId !== this.lastParams?.layer || restParams.weight !== this.lastParams?.weight) {
          const dataSourceName = this.layers[layerId]?.data_source_name;
          const idAttribute = this.datasources[dataSourceName]?.attributes_schema?.id_name;
          const filter = this.getFilter(layerId);
          let min, max;
          if (restParams.weight) {
            console.log(restParams.weight);
            const stats = await this.getDataSourceAttributeStatistics({
              dataSourceName,
              attributeName: restParams.weight,
              params: { features_filter: filter },
            });
            min = stats?.min;
            max = stats?.max;
          }
          const r = await this.getV2DatasourceFeatures({
            datasource: dataSourceName,
            payload: {
              attributes: [...(restParams.weight ? [restParams.weight] : [idAttribute])],
              filter_expression: filter,
            },
          });
          features = new GeoJSON().readFeatures(
            {
              ...(restParams.weight
                ? {
                    ...r,
                    features: r.features.map(feature => {
                      return {
                        ...feature,
                        properties: {
                          ...feature.properties,
                          [restParams.weight]: (feature.properties[restParams.weight] - min) / (max - min),
                        },
                      };
                    }),
                  }
                : r),
              type: 'FeatureCollection',
            },
            {
              dataProjection: this.$_config.defaultEpsg,
              featureProjection: this.$_config.defaultEpsg,
            }
          );
        }
        this.$root.$emit('toggleHeatmap', { layerId, params: restParams, features });
        this.lastParams = JSON.parse(JSON.stringify(params));
        localStorage.setItem('heatmap-params', JSON.stringify({ layer: layerId, weight: restParams.weight }));
      } finally {
        this.loading = false;
      }
    },
    getFilter(layerId) {
      const tableFilter = this.layersFilters[layerId]?.filterExpression || null;
      const layerFilter = this.layers[layerId]?.filter_expression;
      return layerFilter && tableFilter
        ? { '!AND': [layerFilter, tableFilter] }
        : { ...(layerFilter ? layerFilter : {}), ...tableFilter };
    },
    onParamChange(input) {
      if (input.name === 'layer') this.params.weight = '';
    },
  },
  watch: {
    isMenuOpen(nV) {
      if (!nV) {
        this.$root.$emit('toggleHeatmap');
        this.lastParams = undefined;
      }
    },
    pointProjectLayers: {
      handler() {
        if (this.params.layer && !this.pointProjectLayers.find(l => l.id === this.params.layer)) {
          this.params.layer = null;
          this.params.weight = '';
          this.$refs.form?.resetValidation();
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    const heatmapParams = JSON.parse(localStorage.getItem('heatmap-params') || '{}');
    if (this.pointProjectLayers.find(l => l.id === heatmapParams.layer))
      this.params = { ...this.params, ...heatmapParams };
  },
};
</script>
